/**
 * LoadReportTable.jsx
 *
 * data-fit LLC — DecisionFlow
 * Stage 1 LOAD — Sub-component of ValidationLoadReport (LOAD-02)
 * Branch: feature/compliance-lifecycle-foundation
 *
 * Tabbed error table with inline cell editing and AI fix panel.
 * Inline edits are client-side only — no API calls per edit.
 * grouped_fixes rendered only when aiStatus === 'ready' and array
 * is non-empty (null-safe guard per spec G-F03).
 * confidence field displayed as percentage (0.0–1.0 → 0%–100%).
 *
 * OI-BOOTSTRAP-CSV-SPEC: per-column validation rules are stubbed.
 * Spec authority: ComponentSpec_LOAD_corrected_S86g.md
 * Path: ui/src/components/tenant-admin/LoadReportTable.jsx
 * M040 C2: hardcoded colors → tokens, text-* → text-tok-*, all <td> get text-[rgb(var(--color-text))]
 */

import React, { useMemo, useState } from 'react';

const SEVERITY_ORDER = { critical: 3, warning: 2, info: 1 };

// Severity badge styles — M040 tokens
const severityStyles = {
  critical: { icon: '●', label: 'Critical', className: 'bg-[rgb(var(--color-danger))]/10 text-[rgb(var(--color-danger))] ring-[rgb(var(--color-danger))]/30' },
  warning:  { icon: '◐', label: 'Warning',  className: 'bg-[rgb(var(--color-warning))]/10 text-[rgb(var(--color-warning))] ring-[rgb(var(--color-warning))]/30' },
  info:     { icon: 'ℹ', label: 'Info',     className: 'bg-[rgb(var(--color-info))]/10 text-[rgb(var(--color-info))] ring-[rgb(var(--color-info))]/30' },
};

function formatConfidence(confidence) {
  if (typeof confidence !== 'number') return '0%';
  return `${Math.round(confidence * 100)}%`;
}

function SeverityBadge({ severity }) {
  const config = severityStyles[severity] || severityStyles.info;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-tok-xs font-semibold ring-1 ${config.className}`} aria-label={config.label}>
      <span aria-hidden="true">{config.icon}</span>
      {config.label}
    </span>
  );
}

function EditableCell({ error, value, onEditCell }) {
  const [draftValue, setDraftValue] = useState(value || '');
  return (
    <input
      type="text"
      value={draftValue}
      onChange={(e) => setDraftValue(e.target.value)}
      onBlur={() => onEditCell(error.file, error.row, error.column, draftValue)}
      aria-label={`Edit ${error.column} for row ${error.row}`}
      className="w-full min-w-40 rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] px-3 py-2 text-tok-sm shadow-sm focus:outline-none"
    />
  );
}

function GroupedFixCard({ fix, onApplyAIFix, onDismissFix }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <article className="rounded-2xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))]">Grouped Fix</p>
          <h4 className="mt-1 text-tok-sm font-semibold text-[rgb(var(--color-text))]">{fix.error_code}</h4>
        </div>
        <span className="rounded-full bg-[rgb(var(--color-surface))] px-2.5 py-1 text-tok-xs font-semibold text-[rgb(var(--color-text-muted))] ring-1 ring-[rgb(var(--color-border))]">
          {formatConfidence(fix.confidence)}
        </span>
      </div>
      <dl className="mt-3 space-y-2 text-tok-sm">
        <div><dt className="font-medium text-[rgb(var(--color-text-muted))]">Affected rows</dt><dd className="text-[rgb(var(--color-text))]">{fix.affected_rows}</dd></div>
        <div><dt className="font-medium text-[rgb(var(--color-text-muted))]">Suggestion</dt><dd className="text-[rgb(var(--color-text))]">{fix.suggestion}</dd></div>
        {fix.basis && <div><dt className="font-medium text-[rgb(var(--color-text-muted))]">Basis</dt><dd className="text-[rgb(var(--color-text))]">{fix.basis}</dd></div>}
      </dl>
      {expanded && (
        <div className="mt-4 rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-3 text-tok-sm text-[rgb(var(--color-text-muted))]">
          Preview changes for this fix are derived from affected server validation rows and current in-memory edits.
        </div>
      )}
      <div className="mt-4 flex flex-wrap gap-2">
        <button type="button" onClick={() => onApplyAIFix(fix)} className="rounded-xl bg-[rgb(var(--color-primary))] px-3 py-2 text-tok-xs font-semibold text-white hover:bg-[rgb(var(--color-primary-hover))] focus:outline-none">
          Apply to all {fix.affected_rows} rows
        </button>
        <button type="button" onClick={() => setExpanded((c) => !c)} className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-xs font-semibold text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] focus:outline-none">
          {expanded ? 'Hide preview' : 'Preview changes'}
        </button>
        <button type="button" onClick={() => onDismissFix(fix.error_code)} className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-xs font-semibold text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] focus:outline-none">
          Dismiss
        </button>
      </div>
    </article>
  );
}

export default function LoadReportTable({
  errors, groupedFixes, inMemoryEdits, tier,
  onEditCell, onApplyAIFix, onDismissFix, onSeverityFilter,
  activeFile, severityFilter = 'all', summary = null,
}) {
  const sortedErrors = useMemo(() => {
    return [...(errors || [])].sort((a, b) => {
      const severityDelta = (SEVERITY_ORDER[b.severity] || 0) - (SEVERITY_ORDER[a.severity] || 0);
      if (severityDelta !== 0) return severityDelta;
      return (a.row || 0) - (b.row || 0);
    });
  }, [errors]);

  const totalRows   = summary?.total_rows     || 0;
  const criticalRows = summary?.rows_critical || 0;
  const warningRows  = summary?.rows_warning  || 0;
  const validRows    = summary?.rows_valid    || 0;

  return (
    <div className="space-y-5">
      {/* Validation summary */}
      <section aria-label="Validation summary" className="grid grid-cols-2 gap-3 rounded-2xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-4 md:grid-cols-4">
        <div>
          <p className="text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))]">Total</p>
          <p className="mt-1 text-tok-lg font-semibold text-[rgb(var(--color-text))]">{totalRows}</p>
        </div>
        <div>
          <p className="text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-danger))]">Critical</p>
          <p className="mt-1 text-tok-lg font-semibold text-[rgb(var(--color-danger))]">● {criticalRows}</p>
        </div>
        <div>
          <p className="text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-warning))]">Warnings</p>
          <p className="mt-1 text-tok-lg font-semibold text-[rgb(var(--color-warning))]">◐ {warningRows}</p>
        </div>
        <div>
          <p className="text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-success))]">Valid</p>
          <p className="mt-1 text-tok-lg font-semibold text-[rgb(var(--color-success))]">✓ {validRows}</p>
        </div>
      </section>

      {/* Error table */}
      <section className="rounded-2xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))]">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[rgb(var(--color-border))] p-4">
          <div>
            <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Error Table</h3>
            <p className="mt-1 text-tok-sm text-[rgb(var(--color-text-muted))]">
              Showing validation issues for <span className="font-semibold">{activeFile}</span>.
            </p>
          </div>
          <label className="flex items-center gap-2 text-tok-sm font-medium text-[rgb(var(--color-text))]">
            Severity
            <select value={severityFilter} onChange={(e) => onSeverityFilter(e.target.value)} className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] px-3 py-2 text-tok-sm focus:outline-none">
              <option value="all">All</option>
              <option value="critical">Critical</option>
              <option value="warning">Warning</option>
              <option value="info">Info</option>
            </select>
          </label>
        </div>
        <div className="overflow-x-auto">
          <table role="grid" aria-label={`Validation errors for ${activeFile}`} className="min-w-full divide-y divide-[rgb(var(--color-border))]">
            <thead className="bg-[rgb(var(--color-surface-muted))]">
              <tr>
                {['Row #', 'Column', 'Severity', 'Error code', 'Message', 'AI suggestion', 'Edit'].map((h) => (
                  <th key={h} scope="col" className="px-4 py-3 text-left text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))]">
              {sortedErrors.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-tok-sm text-[rgb(var(--color-text-muted))]">No validation issues match this filter.</td></tr>
              )}
              {sortedErrors.map((error, index) => {
                const currentValue = inMemoryEdits?.[error.file]?.[error.row]?.[error.column] || '';
                return (
                  <tr key={`${error.file}-${error.row}-${error.column}-${error.code}-${index}`} className={error.severity === 'critical' ? 'bg-[rgb(var(--color-danger))]/5' : 'hover:bg-[rgb(var(--color-surface-muted))]'}>
                    <td className="whitespace-nowrap px-4 py-3 text-tok-sm font-medium text-[rgb(var(--color-text))]">{error.row}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-tok-sm text-[rgb(var(--color-text))]">{error.column}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-[rgb(var(--color-text))]"><SeverityBadge severity={error.severity} /></td>
                    <td className="whitespace-nowrap px-4 py-3 text-tok-sm font-mono text-[rgb(var(--color-text))]">{error.code}</td>
                    <td className="min-w-72 px-4 py-3 text-tok-sm leading-6 text-[rgb(var(--color-text))]">{error.message}</td>
                    <td className="min-w-64 px-4 py-3 text-tok-sm leading-6 text-[rgb(var(--color-text))]">
                      {error.ai_suggestion || <span className="text-[rgb(var(--color-text-muted))]">No row-level suggestion</span>}
                    </td>
                    <td className="min-w-56 px-4 py-3 text-[rgb(var(--color-text))]">
                      <EditableCell error={error} value={currentValue} onEditCell={onEditCell} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* AI Fix Panel */}
      <section className="rounded-2xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-4">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">AI Fix Panel</h3>
            <p className="mt-1 text-tok-sm text-[rgb(var(--color-text-muted))]">Grouped fixes are sourced from the AI query response, not row-level validation hints.</p>
          </div>
          <span className="rounded-full bg-[rgb(var(--color-surface-muted))] px-3 py-1 text-tok-xs font-semibold capitalize text-[rgb(var(--color-text-muted))]">{tier}</span>
        </div>
        {groupedFixes?.length > 0 ? (
          <div className="grid gap-3">
            {groupedFixes.map((fix) => (
              <GroupedFixCard key={fix.error_code} fix={fix} onApplyAIFix={onApplyAIFix} onDismissFix={onDismissFix} />
            ))}
          </div>
        ) : (
          <div className="rounded-2xl border border-dashed border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-5 text-tok-sm text-[rgb(var(--color-text-muted))]">
            No grouped suggestions available for this file/filter context.
          </div>
        )}
      </section>
    </div>
  );
}
